import React, { useEffect, useState, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { Button, Flex, Image } from "@chakra-ui/react";
import clsx from "clsx";
import { pdf } from "@react-pdf/renderer";
import { datadogLogs } from "@datadog/browser-logs";

import useStore from "@/stores/index";
import { getFeatures } from "@/stores/classwizeConfigStore";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";

import { envConfig } from "@/config";
import { getChatDisabled, isPlatformChatSupported } from "@/js/utils/chatUtils";
import { userDisplayText } from "@/js/utils/userUtils";
import CloseFullscreenIcon from "@/assets/closeFullscreenIcon.svg?url";
import { ChatButton } from "@/js/components/ClassroomChat/components/ChatButton";
import ChatWindow from "@/js/components/ClassroomChat/components/ChatWindow";
import P2PScreenWrapper from "@/js/components/P2PDisplayModal/components/P2pScreenWrapper";
import { useP2p, useChat } from "@/js/components/P2PDisplayModal/hooks";
import ConfirmScreenshotPopup from "@/js/components/P2PDisplayModal/components/PDFSnapshot/ConfirmScreenshotPopup";
import PDFSnapshotDocument from "@/js/components/P2PDisplayModal/components/PDFSnapshot/PDFSnapshotDocument";
import { getPeersConnectionType } from "@/js/components/P2PDisplayModal/helpers";
import { ActionButton } from "@/js/components/Classrooms/ActionButton";
import { ChakraFAIcon } from "@/components/ui/Icon";

//! Should be conditionally rendered inside parent as this component requires some setup logic that uses hooks (rules of hooks states to not use hooks conditionally)
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const AgentP2PModal = (props) => {
	const features = getFeatures(useStore.getState());
	const [fullscreenMode, setFullscreenMode] = useState(false);
	const [showChat, toggleShowChat] = useState(false);
	const [screenshotAvailable, setScreenshotAvailable] = useState(false);
	const isUserPaused = useClassroomPolicyStore.getState().getPausedStudents().includes(props.user.username);

	const [streamReady, setStreamReady] = useState(false);
	const videoElementsRef = useRef([]);
	const playingVideoElementRef = useRef();
	const tracksRef = useRef([]);
	const canvasRef = useRef(null);
	const connectionStateRef = useRef("new");
	const hasInitializedRef = useRef(false);

	// This is used for a patching an issue where the video stream may duplicate if the peer connection disconnects and then
	// reconnects in a short time frame. The design of Live View is not robust enough to be resilient to this, it makes the assumption
	// that a connection is only formed once, and if the connection fails the user is asked to re-open the modal, which starts Live View fresh.
	const hasConnectedRef = useRef(false);

	const currentVideoFrame = document.querySelector("video");

	const chat = useChat(props.classroom, props.user);

	const [snapshotDialogueOpen, setSnapshotDialogueOpen] = useState(false);
	const [storedSnapshot, setStoredSnapshot] = useState();

	const peerConnectionCloseRef = useRef();
	const dataChannelCloseRef = useRef();
	const heartbeatIntervalRef = useRef();

	const debugStartTimestampRef = useRef(Date.now());
	const debugFirstConnectedTimestampRef = useRef();
	const debugFirstHeartbeatDataChannelOpenTimestampRef = useRef();
	const debugFirstVideoPlayTimestampRef = useRef();
	const debugFirstConnectedTypeRef = useRef();

	const p2p = useP2p(props.open, props.peerAgentId, props.user, "laptop", (message) => {
		switch (message) {
			case "STUDENT_NOT_SIGNALED":
				datadogLogs.logger.log(`Agent Live View - student not connected to signaler after 5 seconds`);
				break;
			case "CONNECTION_NOT_ESTABLISHED":
				datadogLogs.logger.log(`Agent Live View - connection not established after 10 seconds`);
				break;
			default:
				console.error("Agent Live View - connection is taking a long time...");
		}
	});

	const disabledChat = useMemo(() => {
		const classroomChatDisabled = getChatDisabled(props.classroom.name);
		const platformChatSupported = isPlatformChatSupported(props.window_data?.[0]);
		return classroomChatDisabled || !platformChatSupported;
	}, [props.classroom.name, props.window_data]);

	// cleanup live view on unmount
	useEffect(() => {
		datadogLogs.logger.info("Agent Live View - starting");

		//workaround to remove the lint error
		const getStartTime = () => {
			return debugStartTimestampRef.current;
		};

		return () => {
			datadogLogs.logger.info("Agent Live View - closing");
			if (peerConnectionCloseRef.current) {
				peerConnectionCloseRef.current();
			}

			if (dataChannelCloseRef.current) {
				dataChannelCloseRef.current();
			}

			if (getStartTime() && !debugFirstVideoPlayTimestampRef.current) {
				// The video has not started yet
				const now = Date.now();
				const deltaStartSeconds = (now - getStartTime()) / 1000;
				datadogLogs.logger.log(`Agent Live View - teacher closed live view before video started, after ${deltaStartSeconds} seconds`, {
					sinceStart: deltaStartSeconds,
				});
			}
		};
	}, []);

	useEffect(() => {
		if (p2p.peerConnection && !hasInitializedRef.current) {
			hasInitializedRef.current = true;

			for (let i = 0; i < 4; i++) {
				p2p.peerConnection.pc.addTransceiver("video", { direction: "recvonly" });
			}

			const dataChannel = p2p.peerConnection.pc.createDataChannel("heartbeat");

			// start sending heartbeat messages when the data channel is open
			dataChannel.addEventListener("open", () => {
				// send a heartbeat every HEARTBEAT_INTERVAL, HEARTBEAT_TIMEOUT_MS determines how
				// long the agent will wait for another heartbeat before closing the connection.
				heartbeatIntervalRef.current = setInterval(() => {
					dataChannel.send(JSON.stringify(envConfig.LIVE_VIEW_HEARTBEAT_TIMEOUT_MS));
				}, envConfig.LIVE_VIEW_HEARTBEAT_INTERVAL);
			});

			// logging for heartbeat data channel open state
			dataChannel.addEventListener("open", () => {
				// log timings for first data channel open state
				if (!debugFirstHeartbeatDataChannelOpenTimestampRef.current) {
					debugFirstHeartbeatDataChannelOpenTimestampRef.current = Date.now();
					const startDeltaSeconds = (debugFirstHeartbeatDataChannelOpenTimestampRef.current - debugStartTimestampRef.current) / 1000;
					const connectDeltaSeconds =
						(debugFirstHeartbeatDataChannelOpenTimestampRef.current - debugFirstConnectedTimestampRef.current) / 1000;
					datadogLogs.logger.log(
						`Agent Live View - heartbeat datachannel state change: open (first). sinceStart=${startDeltaSeconds} sinceConnected=${connectDeltaSeconds}`,
						{ sinceStart: startDeltaSeconds, sinceConnected: connectDeltaSeconds }
					);
				} else {
					// log subsequent data channel open state after first
					datadogLogs.logger.log(`Agent Live View - heartbeat datachannel state change: open`);
				}
			});

			dataChannel.onerror = (event) => {
				console.error(`Agent Live View - datachannel error event, error message: '${event.error.message}'`);
			};

			p2p.peerConnection.pc.ontrack = (event) => {
				// if we already know about this track, don't add it again as it will result in a duplicate video element.
				if (tracksRef.current.find((track) => track.id === event.track.id)) {
					datadogLogs.logger.warn("Agent Live View - track already exists, not adding it again");
					return;
				}
				tracksRef.current = [...tracksRef.current, event];

				// detect when the stream has frozen
				event.track.onmute = () => {
					if (tracksRef.current.every((track) => track.muted)) {
						datadogLogs.logger.error(`Agent Live View - all media tracks are in 'muted' state, resulting in the video stream being frozen`);
					}
				};
			};

			// setup the video element when the first connected event is received
			p2p.peerConnection.pc.addEventListener("connectionstatechange", () => {
				switch (p2p.peerConnection.pc.connectionState) {
					case "connected":
						if (connectionStateRef.current === "connected") {
							datadogLogs.logger.warn(
								`Agent Live View - RTCPeerConnection state change 'connected' received even though the current state was already 'connected'. Ignoring this connected event'`
							);
							break;
						}

						if (!hasConnectedRef.current) {
							hasConnectedRef.current = true;
							onConnected(p2p.peerConnection);
						} else {
							datadogLogs.logger.warn(
								"Agent Live View - multiple connection events received in one session, connection has already been configured. Ignoring this event."
							);
						}
						break;
					default:
						break;
				}
				connectionStateRef.current = p2p.peerConnection.pc.connectionState;
			});

			// logging for different connection states
			p2p.peerConnection.pc.addEventListener("connectionstatechange", async () => {
				switch (p2p.peerConnection.pc.connectionState) {
					case "connected":
						// log timings for first connection state
						if (!debugFirstConnectedTimestampRef.current) {
							debugFirstConnectedTimestampRef.current = Date.now();
							const deltaSeconds = (debugFirstConnectedTimestampRef.current - debugStartTimestampRef.current) / 1000;
							const connectionType = await getPeersConnectionType(p2p.peerConnection.pc);
							debugFirstConnectedTypeRef.current = connectionType;
							datadogLogs.logger.info(`Agent Live View - RTCPeerConnection state change: connected (first). sinceStart=${deltaSeconds}`, {
								sinceStart: deltaSeconds,
								teacherCandidateType: connectionType.local,
								studentCandidateType: connectionType.remote,
							});
						} else {
							// log subsequent connect state after first
							datadogLogs.logger.info("Agent Live View - RTCPeerConnection state change: connected");
						}
						break;
					case "disconnected":
						datadogLogs.logger.info("Agent Live View - RTCPeerConnection state change: disconnected");
						break;
					case "closed": {
						datadogLogs.logger.info("Agent Live View - RTCPeerConnection state change: closed");
						break;
					}
					case "failed": {
						console.error(`Agent Live View - RTCPeerConnection state change: failed`);
						break;
					}
					default:
						break;
				}
			});

			p2p.peerConnection.pc.addEventListener("icecandidateerror", (event) => {
				console.error(
					`Agent Live View - RTCPeerConnection ice candidate error: ${event.errorText}, errorCode: ${event.errorCode}, url: ${event.url}`
				);
			});

			p2p.peerConnection.pc.addEventListener("iceconnectionstatechange", (event) => {
				datadogLogs.logger.log(`Agent Live View - RTCPeerConnection ice connection state change: ${event.target.iceConnectionState}`);
			});

			p2p.peerConnection.pc.addEventListener("signalingstatechange", (event) => {
				datadogLogs.logger.log(`Agent Live View - RTCPeerConnection signaling state change: ${event.target.signalingState}`);
			});

			// cleanup handlers
			peerConnectionCloseRef.current = () => {
				if (videoElementsRef.current) {
					videoElementsRef.current.forEach((video) => {
						video.onplay = null;
						if (video.srcObject) {
							const tracks = video.srcObject.getTracks();
							if (tracks) {
								for (const i in tracks) {
									const track = tracks[i];
									track.stop();
								}
							}
						}
						try {
							video.removeAttribute("srcObject");
						} catch {
							console.warn("could not remove stream");
						}
						video.remove();
					});
				}
				videoElementsRef.current = [];
				tracksRef.current = [];
				peerConnectionCloseRef.current = null;
			};

			dataChannelCloseRef.current = () => {
				// stop the heartbeat when the channel closes
				if (heartbeatIntervalRef.current) {
					clearInterval(heartbeatIntervalRef.current);
				}
				heartbeatIntervalRef.current = null;

				dataChannel.close();
				dataChannelCloseRef.current = null;
			};
		}
	}, [p2p.connectionStartTime, p2p.peerConnection]);

	const onConnected = (peerConnection) => {
		// attach tracks to video elements following connection
		// doing so before the peer connection is in the "completed" state can cause issues:
		// https://www.cloudbees.com/blog/webrtc-issues-and-how-to-debug-them
		for (const i in tracksRef.current) {
			const event = tracksRef.current[i];
			const el = document.createElement(event.track.kind);
			const placeholderElement = document.getElementById("p2pModal_trackPlaceholder");

			placeholderElement.appendChild(el);

			el.autoplay = true;
			el.hidden = true;
			el.srcObject = event.streams[0];

			// listen for video element autoplay event
			el.addEventListener("play", () => {
				playingVideoElementRef.current = el;
				el.hidden = false;
				setStreamReady(true);
				setScreenshotAvailable(true);

				// signal to the agent that the video has started - so keyframe frequency can be reduced.
				peerConnection.sendReadyMessage();
			});

			el.addEventListener("play", () => {
				if (!debugFirstVideoPlayTimestampRef.current) {
					// log first video play
					const now = Date.now();
					debugFirstVideoPlayTimestampRef.current = now;
					const deltaStartSeconds = (now - debugStartTimestampRef.current) / 1000;
					const deltaConnectSeconds = (now - debugFirstConnectedTimestampRef.current) / 1000;
					datadogLogs.logger.log(
						`Agent Live View - video play event (first). sinceStart=${deltaStartSeconds} sinceConnected=${deltaConnectSeconds}`,
						{
							sinceStart: deltaStartSeconds,
							sinceConnected: deltaConnectSeconds,
						}
					);
				} else {
					// log video plays after first
					datadogLogs.logger.log(`Agent Live View - video play event`);
				}
			});
		}
	};

	const handleClose = () => {
		// clear selected thread
		useStore.getState().chat.toggleChatWindow(true);
		props.close();
	};

	const openFullscreen = () => {
		if (playingVideoElementRef.current && streamReady) {
			playingVideoElementRef.current.requestFullscreen();
		}
	};

	const screenshotAccepted = () => {
		localStorage.setItem(envConfig.SCREENSHOT_ACCEPT_LOCALSTORAGE_KEY, Date.now() / 1000);
	};

	const setScreenshotPopupOpen = (val) => {
		setSnapshotDialogueOpen(val);
	};

	const onClickSnapshotButton = () => {
		if (snapshotDialogueOpen) {
			setSnapshotDialogueOpen(false);
			return;
		}

		let snapshot = props.activeTab.screenshot;
		if (screenshotAvailable) {
			// draw image from the video frame
			const context = canvasRef.current.getContext("2d");
			canvasRef.current.width = currentVideoFrame.videoWidth;
			canvasRef.current.height = currentVideoFrame.videoHeight;
			context.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
			context.drawImage(currentVideoFrame, 0, 0, canvasRef.current.width, canvasRef.current.height);
			snapshot = canvasRef.current.toDataURL();
		}

		const { active_device, active_username, deviceinfo } = useUserSessionStore.getState();
		const date = new Date();
		const studentName = userDisplayText(props.user);
		const dateString = `${date.getFullYear()}${date.getMonth()}${date.getDate()}`;
		const timeString = `${date.getHours()}${date.getMinutes()}${date.getSeconds()}`;
		const niceTimeString = `${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;

		const useSmoothwallBranding = useUserSessionStore.getState().deviceinfo.customer_internal_company === "smoothwall";

		const snapshotData = {
			image: snapshot,
			timestamp: `${date.toLocaleString("default", {
				month: "long",
			})} ${date.getDate()} ${date.getFullYear()}, ${niceTimeString}`,
			filename: `${studentName.replace(/\s/g, "")}_${dateString}_${timeString}.pdf`,
			deviceName: deviceinfo?.user_defined_name || active_device,
			deviceId: active_device,
			class: props.classroom.name,
			teacher: active_username,
			studentName: studentName,
			studentUsername: props.user.username,
			platform: props.platform,
			mac: props.peerAgentId,
			smoothwall: useSmoothwallBranding,
		};

		const agreeTime = localStorage.getItem(envConfig.SCREENSHOT_ACCEPT_LOCALSTORAGE_KEY);

		// if the user has agreed to the download in the last 24 hours, automatically download the PDF
		if (agreeTime && Date.now() / 1000 < parseFloat(agreeTime) + 86400) {
			pdf(<PDFSnapshotDocument {...snapshotData} />)
				.toBlob()
				.then((blob) => {
					const url = window.URL.createObjectURL(blob);
					const a = document.createElement("a");
					a.href = url;
					a.download = snapshotData.filename;
					a.click();
				});
			setSnapshotDialogueOpen(false);
			return;
		}
		setSnapshotDialogueOpen(true);
		setStoredSnapshot(snapshotData);
	};

	const handleClickRuleButton = () => {
		props.openRuleModal(false, undefined, undefined, props.user.username);
	};

	const renderButtons = () => {
		const showButton = playingVideoElementRef.current && streamReady;
		return (
			<Flex gap={2}>
				{!props.isMonitoringClass && features.includes("policies") && !props.studentIsFocused && (
					<Button onClick={handleClickRuleButton} gap={2}>
						<ChakraFAIcon icon="fa-pencil" variant="solid" />
						Create rule
					</Button>
				)}
				<canvas className="P2PDisplayModal_hiddenContainer" ref={canvasRef} />
				<ConfirmScreenshotPopup
					tooltipOpen={snapshotDialogueOpen}
					filename={storedSnapshot?.filename}
					document={storedSnapshot && <PDFSnapshotDocument {...storedSnapshot} />}
					onAccept={screenshotAccepted}
					onPopupVisibleChange={setScreenshotPopupOpen}
				>
					<Button
						aria-describedby="download-screenshot-confirmation"
						onClick={onClickSnapshotButton}
						isDisabled={snapshotDialogueOpen}
						gap={2}
					>
						<ChakraFAIcon icon="fa-camera" variant="solid" />
						Screenshot
					</Button>
				</ConfirmScreenshotPopup>
				<Button onClick={openFullscreen} isDisabled={!showButton} gap={2}>
					<ChakraFAIcon icon="fa-up-right-and-down-left-from-center" variant="solid" />
					{fullscreenMode ? "Exit full screen" : "Full screen"}
				</Button>
			</Flex>
		);
	};

	const renderScreen = () => {
		return (
			<div className={"P2PDisplayModal_screenBox--agent"}>
				<div id="p2pModal_trackPlaceholder" className="P2PDisplayModal_imgWrap">
					{!screenshotAvailable && (
						<div className="P2PDisplayModal_loadingTile">
							<Image src={props.activeTab.screenshot} />
						</div>
					)}
				</div>
			</div>
		);
	};

	if (!props.open) {
		return null;
	}

	const userTitle = `${userDisplayText(props.user)}'s Screen`;

	const handleChatButtonClick = async () => {
		if (!showChat) {
			await useStore.getState().chat.setThreadDetails({
				selectedThreadKey: chat.selectedThreadKey,
				selectedClassId: props.classroom.name,
				selectedStudentName: props.user.username,
			});
		} else {
			useStore.getState().chat.toggleChatWindow(true);
		}
		toggleShowChat(!showChat);
	};

	return (
		<P2PScreenWrapper
			handleClose={handleClose}
			open={props.open}
			fullscreen={fullscreenMode}
			liveViewReady={screenshotAvailable}
			setFullscreenMode={setFullscreenMode}
			title={userTitle}
			exitButton={
				<div className={"P2PDisplayModal_fullscreenExitButton"} onClick={() => setFullscreenMode(false)}>
					<img src={CloseFullscreenIcon} className="P2PDisplayModal_fullscreenExitButtonIcon" alt="Show window fullscreen" />
				</div>
			}
		>
			{fullscreenMode ? (
				renderScreen()
			) : (
				<div className={clsx("P2PDisplayModal_content", showChat && "P2PDisplayModal_content__chatOpen")}>
					<div className="P2PDisplayModal_titleContainer">
						<span className="P2PDisplayModal_title">{userTitle}</span>
						{isUserPaused && <span className="P2PDisplayModal_internetPaused">Internet Paused</span>}
						{p2p.error ? <span className="P2PDisplayModal_label--error">{p2p.error}</span> : null}
						<Flex gap={6} ml={"auto"}>
							{!props.isMonitoringClass && features.includes("open_tab") && (
								<ActionButton
									icon={"fa-browser"}
									label={"Open Site"}
									testId="open-tab-button"
									handleClick={() => props.showOpenTabModal(props.user)}
									disabled={props.studentIsFocused}
								/>
							)}
							{!props.isMonitoringClass && chat.isChatEnabled && !disabledChat && (
								<ChatButton
									handleClick={handleChatButtonClick}
									unreadMessagesCount={chat.unreadMessagesCount}
									hasActiveClass
									hideStatusCircle
								/>
							)}
						</Flex>
					</div>
					<div className="P2PDisplayModal_screenAndChatContainer">
						<div className="P2PDisplayModal_screenContainer">{renderScreen()}</div>
						<div className={clsx("P2PDisplayModal_chatContainer", showChat && "P2PDisplayModal_chatContainer--visible")}>
							{showChat && (
								<ChatWindow
									classroom={props.classroom}
									isChatWorking={!!useStore.getState().chat.unreadMessagesCount.data}
									handleClose={toggleShowChat}
									isEmbedded
								/>
							)}
						</div>
					</div>
					<Flex justifyContent={"space-around"}>{renderButtons()}</Flex>
				</div>
			)}
		</P2PScreenWrapper>
	);
};

AgentP2PModal.propTypes = {
	user: PropTypes.object,
	isMonitoringClass: PropTypes.bool,
	open: PropTypes.bool,
	close: PropTypes.func,
	peerAgentId: PropTypes.string,
	classroom: PropTypes.object,
	openRuleModal: PropTypes.func,
	showOpenTabModal: PropTypes.func,
	studentIsFocused: PropTypes.bool,
	window_data: PropTypes.array,
};

export default AgentP2PModal;
